import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(username, password);
      if (result.success) {
        toast.success('Login successful!');
        navigate(result.redirect || '/admin');
      } else {
        const msg = result.message || 'Incorrect username or password. Please try again.';
        setError(msg);
        toast.error(msg, { duration: 6000 });
      }
    } catch (error) {
      const msg = 'Unable to sign in. Please check your credentials and try again.';
      setError(msg);
      toast.error(msg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
      }}
    >
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Smart Assistant™ Badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-cleona-600 px-4 py-2 mb-6">
            <span className="text-white font-manrope font-semibold text-sm">Smart Assistant™</span>
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
              <svg className="w-3.5 h-3.5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </span>
          </div>

          <h1 className="font-jakarta text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Welcome Back
          </h1>
          <p className="text-gray-600 font-manrope">
            Sign in to access your Smart Assistant™ dashboard.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-red-700 font-manrope">{error}</p>
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                Username or Email
              </label>
              <input
                id="username"
                type="text"
                required
                placeholder="Enter your username or email"
                className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setError(''); }}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                placeholder="Enter your password"
                className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="text-center mt-4">
              <Link
                to="/admin/forgot-password"
                className="text-sm text-cleona-600 hover:text-cleona-700 font-manrope font-medium"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        </div>

        {/* Sign up link */}
        <p className="mt-6 text-center text-gray-600 font-manrope">
          Don't have an account?{' '}
          <Link to="/admin/signup" className="text-cleona-600 hover:text-cleona-700 font-semibold">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
